import { useState, useMemo } from "react";
import { Box, Typography, Card, CardContent, Tabs, Tab } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { SortableTable } from "../components/SortableTable";
import { baseChartOptions } from "../components/chartBase";
import { useData } from "../context/DataContext";

const ALL_DAYS = "Entire Duration";

interface DailySummariesProps {
  onNavigateToVessel: (vessel: string) => void;
}

export default function DailySummaries({ onNavigateToVessel }: DailySummariesProps) {
  const { summaries, heatmapHeaders, rawData } = useData();
  const [selectedDay, setSelectedDay] = useState<string>(ALL_DAYS);

  const isAllDays = selectedDay === ALL_DAYS;

  const dayData = useMemo(
    () => (summaries as Record<string, any[]>)[selectedDay] || [],
    [selectedDay, summaries]
  );

  const entireDuration = useMemo(() => {
    if (!isAllDays) return { avgData: [] as any[], cumulativeData: [] as any[], tableData: [] as any[] };

    const acc: Record<string, {
      captiveUsersSum: number; authPerUserSum: number;
      auth: number; staticDevices: number; unauth: number; wan: number; days: number;
    }> = {};

    for (const r of rawData) {
      if (!acc[r.site]) acc[r.site] = { captiveUsersSum: 0, authPerUserSum: 0, auth: 0, staticDevices: 0, unauth: 0, wan: 0, days: 0 };
      const a = acc[r.site];
      a.captiveUsersSum += r.captiveUsers;
      a.authPerUserSum += r.authPerUser;
      a.auth += r.auth;
      a.staticDevices += r.staticDevices;
      a.unauth += r.unauth;
      a.wan += r.wan;
      a.days++;
    }

    const avgData = Object.entries(acc).map(([site, a]) => ({
      site,
      captiveUsers: Math.round(a.captiveUsersSum / a.days),
      authPerUser: Math.round((a.authPerUserSum / a.days) * 100) / 100,
    })).sort((a, b) => b.authPerUser - a.authPerUser);

    const cumulativeData = Object.entries(acc).map(([site, a]) => ({
      site,
      auth: Math.round(a.auth * 100) / 100,
      staticDevices: Math.round(a.staticDevices * 100) / 100,
      unauth: Math.round(a.unauth * 100) / 100,
      wan: Math.round(a.wan * 100) / 100,
    })).sort((a, b) => b.wan - a.wan);

    const tableData = Object.entries(acc).map(([site, a]) => ({
      site,
      wan: Math.round(a.wan * 100) / 100,
      auth: Math.round(a.auth * 100) / 100,
      staticDevices: Math.round(a.staticDevices * 100) / 100,
      unauth: Math.round(a.unauth * 100) / 100,
      unauthPct: a.wan > 0 ? Math.round((a.unauth / a.wan) * 10000) / 100 : 0,
      captiveUsers: Math.round(a.captiveUsersSum / a.days),
      authPerUser: Math.round((a.authPerUserSum / a.days) * 100) / 100,
    })).sort((a, b) => b.wan - a.wan);

    return { avgData, cumulativeData, tableData };
  }, [isAllDays, rawData]);

  const allSorted = useMemo(
    () => (isAllDays ? entireDuration.cumulativeData : [...dayData].sort((a, b) => b.wan - a.wan)),
    [isAllDays, entireDuration.cumulativeData, dayData]
  );
  const sortedByAuthPerUser = useMemo(
    () => (isAllDays ? entireDuration.avgData : [...dayData].sort((a, b) => b.authPerUser - a.authPerUser)),
    [isAllDays, entireDuration.avgData, dayData]
  );
  const tableData = useMemo(
    () => (isAllDays ? entireDuration.tableData : [...dayData].sort((a, b) => b.wan - a.wan)),
    [isAllDays, entireDuration.tableData, dayData]
  );

  const usageBreakdownOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: "column", height: 320 },
    xAxis: { categories: allSorted.map((r: any) => r.site), labels: { rotation: -55, style: { fontSize: "10px" } } },
    yAxis: { title: { text: "GB" } },
    plotOptions: { column: { stacking: "normal" }, series: { point: { events: { click: function (this: any) { onNavigateToVessel(this.category); } } }, cursor: "pointer" } },
    legend: { enabled: true },
    series: [
      { name: "Auth (Captive)", data: allSorted.map((r: any) => r.auth), color: "#2f2282" },
      { name: "Static", data: allSorted.map((r: any) => r.staticDevices), color: "#3dab97" },
      { name: "UnAuth", data: allSorted.map((r: any) => r.unauth), color: "#d9534f" },
    ],
  };

  const usersAuthOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: "column", height: 320 },
    xAxis: { categories: sortedByAuthPerUser.map((r: any) => r.site), labels: { rotation: -55, style: { fontSize: "10px" } } },
    yAxis: [
      { title: { text: "GB/User" } },
      { title: { text: isAllDays ? "Avg Users" : "Users" }, opposite: true },
    ],
    plotOptions: { series: { point: { events: { click: function (this: any) { onNavigateToVessel(this.category); } } }, cursor: "pointer" } },
    legend: { enabled: true },
    series: [
      { name: "Auth/User", type: "column", data: sortedByAuthPerUser.map((r: any) => r.authPerUser), color: "#2f2282", yAxis: 0 },
      { name: "Captive Users", type: "line", data: sortedByAuthPerUser.map((r: any) => r.captiveUsers), color: "#e8a33d", yAxis: 1 },
    ],
  };

  const rowSx = (_row: any, i: number) => {
    if (i === 0) return { bgcolor: "rgba(245,158,11,0.08)", borderLeft: "3px solid #f59e0b" };
    if (i === 1) return { bgcolor: "rgba(148,163,184,0.1)", borderLeft: "3px solid #94a3b8" };
    if (i === 2) return { bgcolor: "rgba(194,120,3,0.08)", borderLeft: "3px solid #c27803" };
    return {};
  };

  const columns = [
    { key: "rank", header: "#", cell: (_r: any, i: number) => <Box sx={{ color: "text.secondary", fontFamily: "monospace", fontSize: 12 }}>{i + 1}</Box> },
    {
      key: "site", header: "Vessel", sortable: true, sortFn: (a: any, b: any) => a.site.localeCompare(b.site),
      cell: (r: any) => (
        <Box component="span" onClick={() => onNavigateToVessel(r.site)} sx={{ fontFamily: "monospace", fontWeight: 600, color: "primary.main", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
          {r.site}
        </Box>
      ),
    },
    { key: "wan", header: "WAN (GB)", align: "right" as const, sortable: true, sortFn: (a: any, b: any) => a.wan - b.wan, cell: (r: any) => <Box sx={{ fontFamily: "monospace", fontWeight: 600 }}>{r.wan.toFixed(2)}</Box> },
    { key: "auth", header: "Auth (GB)", align: "right" as const, sortable: true, sortFn: (a: any, b: any) => a.auth - b.auth, cell: (r: any) => <Box sx={{ fontFamily: "monospace", color: "#2f2282" }}>{r.auth.toFixed(2)}</Box> },
    { key: "staticDevices", header: "Static (GB)", align: "right" as const, sortable: true, sortFn: (a: any, b: any) => a.staticDevices - b.staticDevices, cell: (r: any) => <Box sx={{ fontFamily: "monospace", color: "#3dab97" }}>{r.staticDevices.toFixed(2)}</Box> },
    { key: "unauth", header: "UnAuth (GB)", align: "right" as const, sortable: true, sortFn: (a: any, b: any) => a.unauth - b.unauth, cell: (r: any) => <Box sx={{ fontFamily: "monospace", color: "#d9534f" }}>{r.unauth.toFixed(2)}</Box> },
    {
      key: "unauthPct", header: "UnAuth %", align: "right" as const, sortable: true, sortFn: (a: any, b: any) => a.unauthPct - b.unauthPct,
      cell: (r: any) => <Box component="span" sx={{ fontFamily: "monospace", px: 0.5, borderRadius: 0.5, bgcolor: r.unauthPct > 15 ? "error.light" : "transparent", color: r.unauthPct > 15 ? "error.contrastText" : "text.primary" }}>{r.unauthPct.toFixed(1)}%</Box>,
    },
    { key: "captiveUsers", header: "Users", align: "right" as const, sortable: true, sortFn: (a: any, b: any) => a.captiveUsers - b.captiveUsers, cell: (r: any) => <Box sx={{ fontFamily: "monospace" }}>{r.captiveUsers}</Box> },
    { key: "authPerUser", header: "Auth/User", align: "right" as const, sortable: true, sortFn: (a: any, b: any) => a.authPerUser - b.authPerUser, cell: (r: any) => <Box sx={{ fontFamily: "monospace" }}>{r.authPerUser.toFixed(2)}</Box> },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
        <Box sx={{ flexShrink: 0 }}>
          <Typography variant="subtitle1" fontWeight={600}>Daily Summaries</Typography>
          <Typography variant="body2" color="text.secondary">Cross-section analysis of fleet activity per day</Typography>
        </Box>
        <Tabs
          value={selectedDay}
          onChange={(_, v) => setSelectedDay(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 36,
            flex: "1 1 auto",
            minWidth: 0,
            maxWidth: "100%",
            "& .MuiTab-root": { minHeight: 36, py: 0.5, fontSize: 12, whiteSpace: "nowrap" },
          }}
        >
          <Tab label="Entire Duration" value={ALL_DAYS} />
          {heatmapHeaders.map(day => <Tab key={day} label={day} value={day} />)}
        </Tabs>
      </Box>

      <Box display="flex" flexDirection="column" gap={2} mb={2}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase" }}>
              Usage Breakdown — Auth / Static / UnAuth {isAllDays ? "(cumulative — sorted by WAN)" : "(sorted by WAN)"}
            </Typography>
            <HighchartsReact highcharts={Highcharts} options={usageBreakdownOptions} />
          </CardContent>
        </Card>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase" }}>
              Captive Users &amp; Auth/User {isAllDays ? "(daily avg — sorted by Auth/User)" : "(sorted by Auth/User)"}
            </Typography>
            <HighchartsReact highcharts={Highcharts} options={usersAuthOptions} />
          </CardContent>
        </Card>
      </Box>

      <SortableTable data={tableData} columns={columns} rowSx={rowSx} defaultSortKey="wan" defaultSortOrder="desc" />
    </Box>
  );
}
